import { useEffect, useState } from 'react';
import { XMarkIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/solid';
import TimePicker from '../../moduleComponents/TimePicker';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';
import ConfirmModal from 'shared_items/ConfirmModal';
import DatePicker from 'shared_items/DatePicker';
import DropdownCustomDark from 'shared_items/DropdownCustomDark';
import {
  CURRENCY_OPTIONS,
  STATUS_OPTIONS,
  emptyTransport,
  nextTempId,
} from './itineraryFormUtils';

const ModalTransportForm = ({
  isOpen,
  transport,
  onClose,
  onSave,
  onDelete,
  isSubmitting = false,
}) => {
  const isNew = !transport;
  const [isEditMode, setIsEditMode] = useState(isNew);
  const [form, setForm] = useState(emptyTransport);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (transport) {
      setForm({ ...transport, _tempId: transport._tempId || nextTempId() });
      setIsEditMode(false);
    } else {
      setForm(emptyTransport());
      setIsEditMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open / transport identity only
  }, [isOpen, transport?._tempId, transport?.id]);

  if (!isOpen) return null;

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave?.(form);
  };

  const readOnly = !isEditMode;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (!showDeleteModal) onClose();
        }}
      />
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col z-10 mx-3"
      >
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            {isNew ? 'New transport' : isEditMode ? 'Edit transport' : 'Transport'}
          </h2>
          <div className="flex items-center gap-1">
            {!isNew && !isEditMode && (
              <button
                type="button"
                onClick={() => setIsEditMode(true)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                title="Edit"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
            )}
            {!isNew && isEditMode && (
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                title="Delete"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            )}
            <button type="button" onClick={onClose} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-3 space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={form.ref_code}
              onChange={(e) => setField('ref_code', e.target.value)}
              placeholder="ref"
              disabled={readOnly}
              className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
            />
            <div className="flex-1">
              <DropdownCustomDark
                options={STATUS_OPTIONS}
                selectedValue={form.status}
                placeholder="status"
                showAllOption
                allOptionText="—"
                allOptionValue=""
                onSelect={(v) => setField('status', v)}
                width="w-full"
                disabled={readOnly}
              />
            </div>
          </div>

          <input
            type="text"
            value={form.mode}
            onChange={(e) => setField('mode', e.target.value)}
            placeholder="Mode (e.g. Avião, Boat)"
            disabled={readOnly}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
          />

          <input
            type="text"
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder="Description"
            disabled={readOnly}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              value={form.origin}
              onChange={(e) => setField('origin', e.target.value)}
              placeholder="Origin"
              disabled={readOnly}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
            />
            <input
              type="text"
              value={form.destination}
              onChange={(e) => setField('destination', e.target.value)}
              placeholder="Destination"
              disabled={readOnly}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex items-center gap-1">
              <DatePicker
                value={form.departure_date}
                onChange={(e) => setField('departure_date', e.target.value)}
                mode="single"
                colorScheme="blue"
                placeholder="Departure"
                disabled={readOnly}
                dropdownZIndex={80}
              />
              <TimePicker
                value={form.departure_time}
                onChange={(e) => setField('departure_time', e.target.value)}
                placeholder="hh:mm"
                disabled={readOnly}
                dropdownZIndex={80}
              />
            </div>
            <div className="flex items-center gap-1">
              <DatePicker
                value={form.arrival_date}
                onChange={(e) => setField('arrival_date', e.target.value)}
                mode="single"
                colorScheme="blue"
                placeholder="Arrival"
                disabled={readOnly}
                dropdownZIndex={80}
              />
              <TimePicker
                value={form.arrival_time}
                onChange={(e) => setField('arrival_time', e.target.value)}
                placeholder="hh:mm"
                disabled={readOnly}
                dropdownZIndex={80}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              value={form.carrier}
              onChange={(e) => setField('carrier', e.target.value)}
              placeholder="Carrier"
              disabled={readOnly}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
            />
            <input
              type="text"
              value={form.booking_ref}
              onChange={(e) => setField('booking_ref', e.target.value)}
              placeholder="Booking ref"
              disabled={readOnly}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              value={form.responsible}
              onChange={(e) => setField('responsible', e.target.value)}
              placeholder="Responsible"
              disabled={readOnly}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
            />
            <input
              type="number"
              step="0.01"
              value={form.cost}
              onChange={(e) => setField('cost', e.target.value)}
              placeholder="Cost"
              disabled={readOnly}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
            />
            <DropdownCustomDark
              options={CURRENCY_OPTIONS}
              selectedValue={form.currency}
              placeholder="Currency"
              onSelect={(v) => setField('currency', v)}
              width="w-full"
              disabled={readOnly}
            />
          </div>

          <textarea
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            placeholder="Notes"
            rows={3}
            disabled={readOnly}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none disabled:bg-gray-50"
          />

          {isEditMode && (
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              {!isNew && (
                <button
                  type="button"
                  onClick={() => {
                    setForm({ ...transport, _tempId: transport._tempId || nextTempId() });
                    setIsEditMode(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 flex items-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting && <SpinnerLoad size="small" />}
                Save
              </button>
            </div>
          )}
        </form>

        {showDeleteModal && (
          <ConfirmModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={() => {
              setShowDeleteModal(false);
              onDelete?.(form);
            }}
            title="Delete transport"
            message="Remove this leg from the trip?"
            confirmText="Delete"
            cancelText="Cancel"
            overlayClassName="z-[80]"
          />
        )}
      </div>
    </div>
  );
};

export default ModalTransportForm;
